/**
 * Configuração de ambiente.
 *
 * Os valores de Keycloak e Diretório são os mesmos do `.env` do `pan-app` do
 * monorepo — o que muda é que aqui eles alimentam `keycloak-js` direto, sem
 * passar por nenhum pacote `@nasajon/erprompt-*`.
 */

const env = import.meta.env;

function texto(valor: unknown, padrao: string): string {
  return typeof valor === 'string' && valor.trim() !== '' ? valor.trim() : padrao;
}

export const config = {
  /**
   * `rest` fala com a API do PAN autenticando no Keycloak (padrão, é o que roda
   * em produção). `demo` roda offline com dados de exemplo, para trabalhar em
   * tela sem depender de login nem de backend.
   */
  dataSource: (env.VITE_PAN_DATA_SOURCE as 'rest' | 'demo' | undefined) ?? 'rest',

  /**
   * API interna do PAN — a única API de domínio que esta aplicação consome.
   *
   * Aponta pro sub-app `v2` do pan-api, não `stable`: `stable` é o contrato
   * antigo (X-Tenant-Id, matriz de 8 papéis) que o pan-app do monorepo ainda
   * usa em produção — `v2` é onde este rewrite (hub, capabilities, tempo
   * real) vive, lado a lado com `stable`, sem risco de quebrar o app antigo.
   *
   * Em desenvolvimento o padrão é o caminho `/pan-api`, servido pelo proxy do
   * Vite: a allowlist de CORS da API tem só a origem de produção, então chamar
   * `api.nasajon.in` direto do `localhost` é bloqueado pelo navegador. Em
   * produção vale a URL absoluta (ver `vite.config.ts`).
   */
  apiBaseUrl: texto(env.VITE_PAN_API_URL, env.DEV ? '/pan-api' : 'https://api.nasajon.in/pan/v2'),

  /**
   * Router `admin` de DENTRO do `v2` (`apps/v2/admin`) — usado só por telas
   * restritas por capability (capabilities, estado da instalação,
   * configuração de agendamento, "Personificar Instalação").
   *
   * Não é o mesmo `/pan/admin` do `pan-admin-app` do monorepo: aquele é o
   * app `apps/admin` legado, com outro conjunto de rotas (usuários, roles,
   * Diretório...). Esta app consome uma cópia isolada dentro do `v2`
   * (`apps/v2/admin`), pra não acoplar em nada que o `pan-admin-app`
   * dependa. Mesmo motivo do proxy de `apiBaseUrl` acima.
   */
  adminBaseUrl: texto(env.VITE_PAN_ADMIN_API_URL, env.DEV ? '/pan-admin-api' : 'https://api.nasajon.in/pan/v2/admin'),

  keycloak: {
    url: texto(env.VITE_KEYCLOAK_URL, 'https://auth.nasajon.com.br/auth'),
    realm: texto(env.VITE_KEYCLOAK_REALM, 'master'),
    clientId: texto(env.VITE_KEYCLOAK_CLIENTID, 'pan-app'),
  },

  basePath: '/',
  kbBaseUrl: texto(env.VITE_PAN_KB_URL, 'https://nasajon.github.io/news/pan/'),
} as const;

/** Capacidade operacional: cada horário comporta até 5 instalações simultâneas. */
export const CAPACIDADE_POR_HORARIO = 5;

/** Janela de manutenção divulgada aos clientes. */
export const JANELA_AGENDAMENTO = { inicio: '07:00', fim: '18:00' } as const;

/** Espelha `RETENCAO_TTL_SEGUNDOS` do backend (`apps/v2/internal/repository.py`). */
export const RETENCAO_TTL_SEGUNDOS = 120;

/** Espelha `ESPACAMENTO_MINIMO_SEGUNDOS` do backend (mesmo arquivo) — instalações
 * do mesmo horário não disparam juntas, cada uma espera isso vezes sua posição
 * na fila do horário. Em minutos aqui porque é só o que a UI precisa somar ao
 * horário exibido; o backend guarda em segundos por ser a unidade do `countdown`
 * do Celery. */
export const ESPACAMENTO_MINIMO_MINUTOS = 2;
